/**
 * useGuideConfig Hook
 *
 * React Query hook for fetching CoPilot Guide configurations.
 */

import { useQuery } from '@tanstack/react-query';
import { useSdkContext } from '../providers/SdkProvider';
import { createGuideService, type GuideFlowId } from '../services/guide.service';

// Query keys
export const guideKeys = {
  all: ['guide'] as const,
  config: (flowId: string) => [...guideKeys.all, 'config', flowId] as const,
};

/**
 * Fetch a CoPilot Guide configuration for a specific flow.
 *
 * @param flowId - The flow identifier (e.g. 'checkup', 'teleportal', 'qultureup', 'journey_builder')
 * @param options - Optional query options
 */
export function useGuideConfig(flowId: GuideFlowId | string, options?: { enabled?: boolean }) {
  const { client } = useSdkContext();
  const service = createGuideService(client);

  return useQuery({
    queryKey: guideKeys.config(flowId),
    queryFn: () => service.getGuideConfig(flowId),
    enabled: options?.enabled !== false && !!flowId,
    staleTime: 30 * 60 * 1000, // 30 minutes — configs change rarely
    gcTime: 60 * 60 * 1000, // 1 hour cache
    retry: 2,
  });
}
